import React, { useState, useMemo, useEffect, useRef } from "react"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { CollapsibleSection } from "@/components/CollapsibleSection"
import { hasCutoff } from "@/lib/preview"
import { CitationsToggle } from "@/components/CitationsToggle"

import { useActiveSection } from "@/lib/useActiveSection"
import { Search, BookOpen, ChevronLeft, ChevronRight, List } from "lucide-react"
import type { DocumentSummary, DocumentDetail } from "@/lib/types"

interface SupplementalListViewProps {
  docs: DocumentSummary[]
  isLoading: boolean
  useDocumentDetail: (
    slug: string
  ) => { data: DocumentDetail | undefined; isLoading: boolean; isError: boolean }
}

function SupplementalContent({ doc, pendingScrollRef }: { doc: DocumentDetail; pendingScrollRef?: React.MutableRefObject<string | null> }) {
  const [showCitations, setShowCitations] = useState(false)
  const hasActivities = doc.activities && doc.activities.length > 0

  const allSectionKeys = hasActivities
    ? doc.activities!.flatMap((a) => a.sections.map((s) => s.section_key))
    : doc.sections.map((s) => s.section_key)
  const activeSection = useActiveSection(allSectionKeys)

  useEffect(() => {
    const sectionKey = pendingScrollRef?.current
    if (!sectionKey) return
    pendingScrollRef.current = null
    requestAnimationFrame(() => {
      document.getElementById(`section-${sectionKey}`)?.scrollIntoView({ behavior: "smooth" })
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Middle: scrollable content */}
      <div className={`flex-1 min-w-0 overflow-auto ${showCitations ? "show-citations" : ""}`}>
        <div className="p-6">
          {/* Title + meta */}
          <div data-sticky-title className="sticky top-0 z-10 -mt-6 mb-6 flex items-start justify-between gap-4 bg-background pb-4 pt-6">
            <div>
              <h2 className="text-2xl font-bold text-[#107D98]">{doc.title}</h2>
              <div className="mt-1 flex items-center gap-2">
                {doc.published_at && (
                  <span className="text-xs text-muted-foreground">
                    Last Updated:{" "}
                    {new Date(doc.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" })}
                  </span>
                )}
                {doc.compliance_tags?.map((tag) => (
                  <span key={tag} className="text-xs text-muted-foreground">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <CitationsToggle
              enabled={showCitations}
              onChange={setShowCitations}
            />
          </div>

          {/* Sections */}
          {hasActivities ? (
            <div className="space-y-6">
              {doc.activities!.map((activity) => (
                <div key={activity.id} className="space-y-4">
                  <h3 className="text-xl font-semibold border-l-4 border-l-primary bg-muted/50 px-4 py-2 rounded-r-md">
                    {activity.name}
                  </h3>
                  {activity.sections.map((section, i) => (
                    <CollapsibleSection
                      key={section.id}
                      section={section}
                      previewCutoff={hasCutoff(doc.preview, activity.sections, i)}
                    />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {doc.sections.map((section, i) => (
                <CollapsibleSection
                  key={section.id}
                  section={section}
                  previewCutoff={hasCutoff(doc.preview, doc.sections, i)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right: TOC */}
      <aside className="hidden w-72 shrink-0 px-6 lg:block xl:w-80">
        <div className="sticky top-0 p-4 pt-6">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <List className="h-4 w-4" />
            Page Contents
          </div>
          <ScrollArea className="h-[calc(100vh-5rem)]">
            <nav className="space-y-0.5">
              {hasActivities
                ? doc.activities!.map((activity) => (
                    <div key={activity.id} className="space-y-0.5">
                      <span className="block py-1 pl-3 text-sm font-medium text-foreground">
                        {activity.name}
                      </span>
                      <div className="ml-2 space-y-0.5">
                        {activity.sections.map((section) => (
                          <a
                            key={section.id}
                            href={`#section-${section.section_key}`}
                            className={`block border-l-2 py-0.5 pl-3 text-sm transition-colors ${
                              activeSection === section.section_key
                                ? "border-l-primary font-medium text-foreground"
                                : "border-l-transparent text-muted-foreground hover:text-foreground"
                            }`}
                            onClick={(e) => {
                              e.preventDefault()
                              document
                                .getElementById(`section-${section.section_key}`)
                                ?.scrollIntoView({ behavior: "smooth" })
                            }}
                          >
                            {section.heading}
                          </a>
                        ))}
                      </div>
                    </div>
                  ))
                : doc.sections.map((section) => (
                    <a
                      key={section.id}
                      href={`#section-${section.section_key}`}
                      className={`block border-l-2 py-1 pl-3 text-sm transition-colors ${
                        activeSection === section.section_key
                          ? "border-l-primary font-medium text-foreground"
                          : "border-l-transparent text-muted-foreground hover:text-foreground"
                      }`}
                      onClick={(e) => {
                        e.preventDefault()
                        document
                          .getElementById(`section-${section.section_key}`)
                          ?.scrollIntoView({ behavior: "smooth" })
                      }}
                    >
                      {section.heading}
                    </a>
                  ))}
            </nav>
          </ScrollArea>
        </div>
      </aside>
    </>
  )
}

function SupplementalDetailPanel({
  slug,
  useDocumentDetail,
  pendingScrollRef,
}: {
  slug: string
  useDocumentDetail: (
    slug: string
  ) => { data: DocumentDetail | undefined; isLoading: boolean; isError: boolean }
  pendingScrollRef?: React.MutableRefObject<string | null>
}) {
  const { data: doc, isLoading, isError } = useDocumentDetail(slug)

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (isError || !doc) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-destructive">Failed to load document</p>
      </div>
    )
  }

  return <SupplementalContent doc={doc} pendingScrollRef={pendingScrollRef} />
}

export function SupplementalListView({
  docs,
  isLoading,
  useDocumentDetail,
}: SupplementalListViewProps) {
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)
  const [query, setQuery] = useState("")
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const pendingScrollRef = useRef<string | null>(null)

  // Deep-link: resolve hash to the owning document on first load
  useEffect(() => {
    const hash = window.location.hash
    if (!hash.startsWith("#section-") || docs.length === 0) return
    const sectionKey = hash.replace("#section-", "")
    const match = docs.find((d) => d.section_keys?.includes(sectionKey))
    if (match && !selectedSlug) {
      pendingScrollRef.current = sectionKey
      setSelectedSlug(match.slug)
    }
  }, [docs]) // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!query.trim()) return docs
    const q = query.toLowerCase()
    return docs.filter(
      (doc) =>
        doc.title.toLowerCase().includes(q) ||
        doc.keywords?.some((kw) => kw.toLowerCase().includes(q)) ||
        doc.compliance_tags?.some((tag) => tag.toLowerCase().includes(q))
    )
  }, [docs, query])

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  return (
    <div className="flex h-full">
      {/* Left: collapsible list panel */}
      <div className={`flex shrink-0 border-r ${sidebarCollapsed ? "" : "w-80"}`}>
        {!sidebarCollapsed && (
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="border-b px-5 py-4">
              <h1 className="text-xl font-semibold">Supplemental Workflows</h1>
              {docs.length > 0 && (
                <div className="relative mt-3">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="pl-9 h-9 text-sm"
                  />
                </div>
              )}
            </div>
            <ScrollArea className="flex-1">
              <div className="space-y-2 p-4">
                {filtered.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-muted-foreground">
                    {query
                      ? "No results found."
                      : "No supplemental workflows are available."}
                  </p>
                ) : (
                  filtered.map((doc) => (
                    <button
                      key={doc.id}
                      onClick={() => setSelectedSlug(doc.slug)}
                      className={`flex w-full items-center justify-between rounded-lg border px-4 py-2.5 text-left text-sm transition-colors ${
                        selectedSlug === doc.slug
                          ? "border-primary bg-card text-foreground"
                          : "border-border bg-background hover:bg-accent"
                      }`}
                    >
                      <div>
                        <span className="font-medium">{doc.title}</span>
                      </div>
                      <ChevronRight className="ml-3 h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        )}
        <button
          onClick={() => setSidebarCollapsed((prev) => !prev)}
          className="flex w-6 shrink-0 items-start justify-center pt-4 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {sidebarCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>
      </div>

      {/* Right: content area (or empty state) */}
      {selectedSlug ? (
        <SupplementalDetailPanel
          key={selectedSlug}
          slug={selectedSlug}
          useDocumentDetail={useDocumentDetail}
          pendingScrollRef={pendingScrollRef}
        />
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center text-muted-foreground">
          <BookOpen className="mb-3 h-12 w-12 opacity-30" />
          <p>Select an item to the left to see</p>
          <p>Supplemental content</p>
        </div>
      )}
    </div>
  )
}
